/**
 * useConditionalFields Hook
 *
 * Manages field visibility, required state, and disabled state
 * based on conditional logic rules and current form values
 */

import { useMemo } from 'react';

import type {
  ConditionalRequirementSetting,
  ConditionalVisibilitySetting,
} from '@sunrise-ui/api/insights';

import type { FieldState, FormFieldRenderConfig, FormValues } from '../types';
import { evaluateFieldState } from '../utils/conditionalLogic';

/**
 * Extended field config with conditional settings
 */
export type FieldWithConditions = FormFieldRenderConfig & {
  visibilitySettings?: ConditionalVisibilitySetting[];
  requirementSettings?: ConditionalRequirementSetting[];
};

type UseConditionalFieldsResult = {
  /** Map of field ID to its current state */
  fieldStates: Record<string, FieldState>;
  /** Get the state for a specific field */
  getFieldState: (fieldId: string) => FieldState;
  /** Check if a field is visible */
  isFieldVisible: (fieldId: string) => boolean;
  /** Check if a field is required */
  isFieldRequired: (fieldId: string) => boolean;
  /** Check if a field is disabled */
  isFieldDisabled: (fieldId: string) => boolean;
  /** Get only the visible fields */
  visibleFields: FieldWithConditions[];
};

const DEFAULT_FIELD_STATE: FieldState = {
  visible: true,
  required: false,
  disabled: false,
};

/**
 * Hook to manage conditional field states based on form values
 *
 * @param fields - Array of field configurations with optional conditional settings
 * @param formValues - Current form values
 * @returns Object with field states and helper functions
 *
 * @example
 * ```tsx
 * const { fieldStates, visibleFields, isFieldRequired } = useConditionalFields(
 *   fields,
 *   formValues
 * );
 *
 * return visibleFields.map(field => (
 *   <DynamicFormField
 *     key={field.id}
 *     field={{
 *       ...field,
 *       required: isFieldRequired(field.id),
 *       disabled: fieldStates[field.id]?.disabled ?? field.disabled,
 *     }}
 *     value={formValues[field.id]}
 *     onChange={handleChange}
 *   />
 * ));
 * ```
 */
export function useConditionalFields(
  fields: FieldWithConditions[],
  formValues: FormValues,
): UseConditionalFieldsResult {
  // Compute field states based on current form values
  const fieldStates = useMemo(() => {
    const states: Record<string, FieldState> = {};

    for (const field of fields) {
      states[field.id] = evaluateFieldState(
        {
          visibilitySettings: field.visibilitySettings,
          requirementSettings: field.requirementSettings,
          defaultVisible: true,
          defaultRequired: field.required ?? false,
          defaultDisabled: field.disabled ?? false,
        },
        formValues,
      );
    }

    return states;
  }, [fields, formValues]);

  // Get state for a specific field
  const getFieldState = useMemo(() => {
    return (fieldId: string): FieldState => {
      return fieldStates[fieldId] ?? DEFAULT_FIELD_STATE;
    };
  }, [fieldStates]);

  // Helper to check if field is visible
  const isFieldVisible = useMemo(() => {
    return (fieldId: string): boolean => {
      return fieldStates[fieldId]?.visible ?? true;
    };
  }, [fieldStates]);

  // Helper to check if field is required
  const isFieldRequired = useMemo(() => {
    return (fieldId: string): boolean => {
      return fieldStates[fieldId]?.required ?? false;
    };
  }, [fieldStates]);

  // Helper to check if field is disabled
  const isFieldDisabled = useMemo(() => {
    return (fieldId: string): boolean => {
      return fieldStates[fieldId]?.disabled ?? false;
    };
  }, [fieldStates]);

  // Filter to only visible fields
  const visibleFields = useMemo(() => {
    return fields.filter((field) => fieldStates[field.id]?.visible ?? true);
  }, [fields, fieldStates]);

  return {
    fieldStates,
    getFieldState,
    isFieldVisible,
    isFieldRequired,
    isFieldDisabled,
    visibleFields,
  };
}
